import { Component, OnInit } from '@angular/core';
import { take, catchError } from 'rxjs/operators';
import moment from 'moment';
import { Product } from 'src/app/order/models/product.model';
import { ProductService } from 'src/app/quoting/services/product.service';
import { Warehouse } from '../../models/warehouse.model';
import { WarehouseLocation } from '../../models/warehouse-location.model';
import { WarehouseService } from '../../services/warehouse.service';
import { WarehouseLocationService } from '../../services/warehouse-location.service';
import { StockQuantityService } from '../../services/stock-quantity.service';
import { ReportCsvService } from '../../services/report-csv.service';
import { WarehousingReportFilters } from '../../services/warehousing-report.service';
import { WarehouseReportingService } from '../../services/warehouse-reporting.service';
import { StockRow, StockOnHandResult, StockValuation } from '../../models/warehouse-reporting.model';

@Component({
    selector: 'app-report-stock-on-hand',
    templateUrl: './report-stock-on-hand.component.html',
    styleUrls: ['./report-stock-on-hand.component.scss'],
    standalone: false
})
export class ReportStockOnHandComponent implements OnInit {

  searchFilters: WarehousingReportFilters = new WarehousingReportFilters();

  warehouses: Warehouse[] = [];
  locations: WarehouseLocation[] = [];
  products: Product[] = [];

  /**
   * Rows replayed from the ledger, NOT the cached `product_stock_level` rows this screen used
   * to list. The level is a cache of the ledger and the two can disagree; a report called
   * "Stock on Hand" was showing the cached number, which is the one the reconciliation report
   * exists to distrust. So the report a reader would naturally believe was reading from the
   * less reliable of the two sources, and could contradict reconciliation without saying so.
   */
  rows: StockRow[] = [];

  /** Server-side totals. Not re-derived here: see `getTotalQty`. */
  totalQty: number = 0;
  valuation: StockValuation = new StockValuation();

  /** Distinguishes "not run yet" from "ran and found nothing". */
  hasRun: boolean = false;
  running: boolean = false;

  constructor(private warehouseReportingService: WarehouseReportingService,
    private warehouseService: WarehouseService,
    private warehouseLocationService: WarehouseLocationService,
    private productService: ProductService,
    private stockQuantityService: StockQuantityService,
    private reportCsvService: ReportCsvService) { }

  ngOnInit(): void {
    this.warehouseService.getAllWarehouses()
    .pipe(take(1))
    .pipe(catchError((e) => {
      if (e.status === 403 || e.status === 401) {
        return e;
      }
      alert('Cannot load warehouses');
      return e;
    }))
    .subscribe((data: Warehouse[]) => {
      this.warehouses = data;
    })

    this.productService.getAllActiveProducts()
    .pipe(take(1))
    .pipe(catchError((e) => {
      if (e.status === 403 || e.status === 401) {
        return e;
      }
      alert('Cannot load products');
      return e;
    }))
    .subscribe((data: Product[]) => {
      this.products = data;
    })
  }

  /**
   * Locations are scoped by warehouseId and the same aisle/shelf exists at more than one
   * site, so the location list only becomes meaningful once a warehouse is chosen.
   */
  onWarehouseChanged(warehouseId: any) {
    this.searchFilters.warehouseId = +warehouseId;
    this.searchFilters.warehouseLocationId = -1;
    this.locations = [];

    if (this.searchFilters.warehouseId === -1) {
      return;
    }

    this.warehouseLocationService.getWarehouseLocationsForWarehouse(this.searchFilters.warehouseId)
    .pipe(take(1))
    .pipe(catchError((e) => {
      if (e.status === 403 || e.status === 401) {
        return e;
      }
      alert('Cannot load warehouse locations');
      return e;
    }))
    .subscribe((data: WarehouseLocation[]) => {
      this.locations = data;
    })
  }

  loadReport() {
    this.running = true;

    this.warehouseReportingService.getStockOnHand(this.searchFilters)
    .pipe(take(1))
    .pipe(catchError((e) => {
      if (e.status === 403 || e.status === 401) {
        return e;
      }
      this.running = false;
      alert('could not get report');
      return e;
    }))
    .subscribe((data: StockOnHandResult) => {
      this.rows = data && data.rows ? data.rows : [];
      this.totalQty = data ? data.totalQty : 0;
      this.valuation = data && data.valuation ? data.valuation : new StockValuation();
      this.hasRun = true;
      this.running = false;
    })
  }

  /**
   * The server's total, not a sum of the rows.
   *
   * Re-adding the column here would give a number that drifts from the endpoint's own by float
   * accumulation, and the two would be printed side by side on the same screen.
   */
  getTotalQty(): string {
    return this.stockQuantityService.round(this.totalQty).toString();
  }

  /** Negative rows are counted separately so the report cannot bury them in a total. */
  getNegativeCount(): number {
    return this.rows.filter((row: StockRow) => row.isNegative).length;
  }

  /**
   * Rows whose stored level disagrees with the ledger replay.
   *
   * This is the reason for the migration. The old screen could not have shown this at all: it
   * only ever had the cached number, so a divergence was invisible here and visible only on the
   * reconciliation report, and the two screens could contradict each other in silence.
   */
  getDriftCount(): number {
    return this.rows.filter((row: StockRow) => !row.matchesLedger).length;
  }

  /** Server-classified. Never re-derive: the tolerance is scaled per location's throughput. */
  isNegative(row: StockRow): boolean {
    return row.isNegative;
  }

  hasDrifted(row: StockRow): boolean {
    return !row.matchesLedger;
  }

  /** Composite key: a row is a (product, location) pair and carries no id of its own. */
  rowKey(row: StockRow): string {
    return row.productId + '@' + row.warehouseLocationId;
  }

  /**
   * "Never priced" and "priced at nothing" are different facts and must not render alike.
   *
   * Keyed off `hasCostBasis`, NOT off the value being null. The endpoint sends `unitCost: 0`
   * and `value: 0` for stock with no cost basis - it does not send null - so a null check
   * silently renders unpriced stock as £0.00, which is the precise confusion the nullable
   * column was introduced to prevent. Caught by reading a screenshot: the table said £0.00
   * on every row while the coverage panel directly above said 168 units had no cost basis.
   */
  formatCost(row: StockRow, value: number): string {
    return row.hasCostBasis ? this.formatMoney(value) : 'Not priced';
  }

  formatMoney(value: number): string {
    if (value === null || value === undefined) {
      return '—';
    }

    const amount = Number(value);

    return (amount < 0 ? '-£' : '£') + Math.abs(amount).toFixed(2);
  }

  formatQty(qty: number): string {
    return this.stockQuantityService.round(qty).toString();
  }

  formatDateTime(value: string): string {
    return value ? moment(value).format('DD/MM/YYYY HH:mm:ss') : '';
  }

  /** True when the money shown covers less than every unit in the result. */
  get valuationIncomplete(): boolean {
    const v = this.valuation;

    return v.unitsWithoutCostBasis > 0
      || v.unitsAtZeroCost > 0
      || v.unitsAtNegativeCost > 0
      || v.unitsFromUncostedMovements > 0;
  }

  exportToCSV() {
    // Header changed with the move to the ledger endpoint, deliberately and with the lead's
    // agreement. The row is a (product, location) pair replayed from the ledger and carries no
    // product_stock_level id, so "Id" is gone; "Qty" is now the ledger quantity rather than the
    // cached level, and "Level Qty" is carried beside it so an export can still show the two
    // disagreeing. Anyone reconciling against an export taken before this will see the change.
    const headers: string[] = ['Product', 'SKU', 'Warehouse', 'Aisle', 'Shelf', 'Qty',
      'Level Qty', 'Matches Ledger', 'Unit Cost', 'Value', 'Cost State', 'Last Movement'];

    const rows: any[][] = this.rows.map((row: StockRow) => [
      row.productName,
      row.sku,
      row.warehouseName,
      row.aisle,
      row.shelf,
      this.formatQty(row.ledgerQty),
      this.formatQty(row.levelQty),
      row.matchesLedger ? 'yes' : 'NO',
      // Blank rather than 0 when there is no cost basis. A spreadsheet sums a zero and ignores
      // a blank, so writing 0 here would let unpriced stock quietly depress an average.
      row.hasCostBasis ? row.unitCost : '',
      row.hasCostBasis ? row.value : '',
      row.costState,
      this.formatDateTime(row.lastMovementAt)
    ]);

    this.reportCsvService.download('stock-on-hand', headers, rows);
  }
}
